import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { twMerge } from 'tailwind-merge';
import { faChevronDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { NetworkIndicator } from '@components/Song/MintComponents/NetworkIndicator';
import { EditionCover } from '@components/basics/EditionCover';
import { SecondaryModalContext, SecondaryModalType } from '@context/secondaryModal';
import type { FragmentType } from '@graphql/reactQuery';
import { ReleaseDetailsFragmentDoc } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { ViewEditionCoverModal } from '../Tiers/ViewEditionCoverModal';
import { ArtistFeeTooltip } from './ArtistFeeTooltip';
import type { SelectedMintPanel } from './Checkout/TieredCheckoutModal';
import { SoundFeeTooltip } from './SoundFeeTooltip';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseDetails on Release {
    id
    title
    chainId
    gaCoverImage {
      id
      url
    }
    vipCoverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    coverImage {
      id
      url
    }
    artist {
      id
      name
    }
  }
`);

export const ReleaseDetails = ({
  releaseData,
  selectedMintPanel,
}: {
  releaseData: FragmentType<ReleaseDetailsFragmentDoc>;
  selectedMintPanel?: SelectedMintPanel;
}) => {
  const {
    title,
    vipCoverImage,
    gaCoverImage,
    staticCoverImage,
    coverImage: { url: coverImageUrl },
    artist: { name: artistName },
  } = getFragment(ReleaseDetailsFragmentDoc, releaseData);

  const { openModal, closeCancelModal } = SecondaryModalContext.useContainer();
  const fallBackImage = staticCoverImage?.url ?? coverImageUrl;

  const coverImage = (() => {
    if (selectedMintPanel === 'LIMITED' && vipCoverImage?.url) {
      return vipCoverImage.url;
    }

    if (selectedMintPanel === 'GA' && gaCoverImage?.url) {
      return gaCoverImage.url;
    }

    return fallBackImage;
  })();

  return (
    <div className="relative flex w-full items-center gap-3 border-b border-base200 pb-3">
      <button
        className="relative block aspect-square h-12 w-12"
        onClick={() => {
          openModal(SecondaryModalType.EDITION_VIEW_COVER, {
            body: (
              <ViewEditionCoverModal
                releaseCover={coverImage}
                releaseTitle={title}
                closeCancelModal={closeCancelModal}
              />
            ),
          });
        }}
      >
        <EditionCover
          releaseTitle={title}
          releaseCover={coverImage}
          imageSize={48}
          className="h-[48px] w-[48px] rounded-md"
        />
      </button>

      <div className="flex flex-col items-start gap-1 overflow-hidden font-medium">
        <h2 className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-xxs text-base800 2xl:text-title-xs">
          {title}
        </h2>
        <h3 className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-xs text-base500 2xl:text-base-s">
          {artistName}
        </h3>
      </div>
    </div>
  );
};

export const PriceDropdown = ({
  chainId,
  totalPriceEthString,
  totalUsdPrice,
  basePriceEthString,
  quantity,
  totalBeforeFeesEthString,
  totalFees,
  totalFeesEthString,
  totalPriceWei,
  isLoading,
  isGA,
}: {
  chainId: number;
  totalPriceEthString: string;
  totalUsdPrice: string;
  basePriceEthString: string;
  quantity: number;
  totalBeforeFeesEthString: string;
  totalFees: bigint | number;
  totalFeesEthString: string;
  totalPriceWei: bigint;
  isLoading: boolean;
  isGA: boolean;
}) => {
  const [open, setOpen] = React.useState(false);

  const GATrigger = (
    <div className="flex min-h-[20px] w-full justify-between">
      <div className="flex items-center gap-0.5 text-base-m text-base800">
        <p className="">Artist & Sound Fee</p>
        <FontAwesomeIcon
          icon={faChevronDown}
          fontSize={12}
          className="p-1 text-base500 group-hover:text-base900 data-[state=closed]:rotate-0 data-[state=open]:rotate-180"
          data-state={open ? 'open' : 'closed'}
        />
      </div>
      <p className="text-right text-base-m text-base800">
        {isLoading ? '...' : totalPriceEthString}
      </p>
    </div>
  );

  const NonGATrigger = (
    <>
      <div className="flex min-h-[40px] w-full justify-between">
        <h2 className="font-title text-title-m font-medium">Total</h2>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-1 ">
            <NetworkIndicator chainId={chainId} size="sm" />
            <h2 className="font-title text-title-m font-medium">
              {isLoading ? '...' : totalPriceEthString}
            </h2>
          </div>
          {!isLoading && totalPriceWei !== 0n && (
            <p className="text-right text-base-m text-base400">~${totalUsdPrice}</p>
          )}
        </div>
      </div>
      <FontAwesomeIcon
        icon={faChevronDown}
        fontSize={12}
        className="p-1 text-base500 group-hover:text-base900 data-[state=closed]:rotate-0 data-[state=open]:rotate-180"
        data-state={open ? 'open' : 'closed'}
      />
    </>
  );
  return (
    <Root
      type="single"
      collapsible
      onValueChange={() => {
        setOpen(open => !open);
      }}
    >
      <Item value="total-price" className="w-full">
        <Header className="flex w-full items-start">
          <Trigger
            className="group flex w-full items-start justify-between gap-1.5 rounded-[6px] py-1"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            {isGA ? GATrigger : NonGATrigger}
          </Trigger>
        </Header>
        <Content
          className={twMerge(
            'pt-2 duration-300 ease-out data-[state=closed]:animate-closeAccordion data-[state=open]:animate-openAccordion',
            isGA ? 'pr-0 text-base500' : 'pr-6 text-base800',
          )}
        >
          <div className="flex items-center justify-between ">
            <p className="text-base-m font-normal ">
              {isGA ? (
                <>
                  Artist Fee <ArtistFeeTooltip />
                </>
              ) : (
                `${basePriceEthString} x ${quantity}`
              )}
            </p>
            <p className="text-right text-base-m font-normal">
              <span>{totalBeforeFeesEthString}</span>
            </p>
          </div>

          {totalFees !== 0n && (
            <div className="flex items-center justify-between py-1">
              <p className="flex items-center gap-1 text-base-m font-normal">
                Sound Fee <SoundFeeTooltip />
              </p>
              <p className="text-right text-base-m font-normal">
                <span>{isLoading ? '...' : totalFeesEthString}</span>
              </p>
            </div>
          )}
        </Content>
      </Item>
    </Root>
  );
};
